import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Check, Calendar, Loader2 } from "lucide-react";
import { cn } from "../../../utils/function";
import useRequest from "../../../components/hook/use-req";

// Shared plumbing for the Campaigns drill-down (Campaigns list -> Campaign
// detail -> Ad set detail -> Ad creative detail), per
// cxo_dashboard_campaigns.postman_collection.json ("CXO Campaigns, Ad sets
// and Creative Detail"). Confirmed metric shape throughout that API:
// `{value, previous, change, change_pct}` (`previous` = same-length period
// immediately before) — read defensively since a few fields come back bare
// instead (e.g. a table row's raw `status`/`roas`).

export type DateRange = "Last 24 Hours" | "Last 7 Days" | "Last 30 Days" | "Quarter to date";
export const DATE_RANGES: DateRange[] = ["Last 24 Hours", "Last 7 Days", "Last 30 Days", "Quarter to date"];
const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
export const fmtShortDate = (d: Date) => d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

export function presetToDates(preset: DateRange): { start_date: string; end_date: string } {
  const end = new Date();
  const start = new Date();
  if (preset === "Last 24 Hours") start.setDate(end.getDate() - 1);
  else if (preset === "Last 7 Days") start.setDate(end.getDate() - 7);
  else if (preset === "Last 30 Days") start.setDate(end.getDate() - 30);
  else start.setMonth(Math.floor(end.getMonth() / 3) * 3, 1);
  return { start_date: fmtDate(start), end_date: fmtDate(end) };
}

export function unwrapValue(raw: any): any {
  if (raw == null) return raw;
  if (typeof raw === "object" && !Array.isArray(raw) && "value" in raw) return raw.value;
  return raw;
}

export interface Metric {
  value: number | null;
  changePct: number | null;
  available: boolean;
}

export function readMetric(raw: any): Metric {
  if (raw == null) return { value: null, changePct: null, available: false };
  if (typeof raw === "object" && raw.available === false) return { value: null, changePct: null, available: false };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const value = unwrapValue(raw) ?? raw.total ?? null;
    const changePct = raw.change_pct ?? raw.delta_pct ?? raw.change ?? null;
    return { value, changePct, available: value != null };
  }
  return { value: raw, changePct: null, available: true };
}

export const formatCount = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : Math.round(Number(v)).toLocaleString();
};
// Postman's own examples show large counts pre-abbreviated (509.2M, 346.7M)
// on the summary cards — this mirrors that rather than spelling out every digit.
// `empty` lets a specific call site opt into "0"/"N/A" instead of the
// default "—" (e.g. the Regions tab's table/map, per direct ask) without
// changing every other tab that already relies on "—".
export const formatCompact = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  if (v == null || isNaN(Number(v))) return empty;
  return new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(Number(v));
};
export const formatCurrency = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : `₦${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
export const formatPct = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : `${Number(v).toFixed(1)}%`;
};
export const formatRoas = (n: any, empty: string = "—") => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? empty : `${Number(v).toFixed(1)}x`;
};
export const formatDelta = (pct: number | null) => (pct == null ? null : `${pct > 0 ? "+" : pct < 0 ? "" : "±"}${pct.toFixed(0)}%`);
export const asText = (raw: any, empty: string = "—"): string => {
  const v = unwrapValue(raw);
  return v == null || v === "" ? empty : String(v);
};

// Generic GET resource — same literal `endpoint` at every call site (React's
// rules of hooks don't allow it to vary within one component), `pathParam`
// (e.g. a campaign id, or "{id}/overview") and `params` (mode/start_date/
// end_date plus whatever filters that endpoint takes) can both change freely.
// Returns the raw parsed body; call sites pull out whatever keys they need
// (`.items`, `.summary`, `.series`, ...) since the shape genuinely differs
// per endpoint rather than forcing one unified extraction.
export function useCxoResource<T = any>(endpoint: string, pathParam: string | undefined, params: Record<string, any>, enabled: boolean = true) {
  const token = localStorage.getItem("authToken");
  const authHeaders = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const { makeRequest, loading } = useRequest(endpoint, "GET", authHeaders);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const paramsKey = JSON.stringify(params);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    const [res, status] = await makeRequest(undefined, params, pathParam);
    if (status >= 200 && status < 300 && res?.status !== false) {
      setData(res);
    } else {
      setError(res?.detail || res?.message || "Couldn't load this.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pathParam, paramsKey, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, reload };
}

export const CXO_AUTH = () => ({ Authorization: `Bearer ${localStorage.getItem("authToken")}` });

// Meta's own effective_status values (confirmed by the doc: "status (Meta
// effective_status)") plus the ad-set-only learning_stage ("LEARNING"),
// shown as its own badge colour in the reference design.
export const STATUS_BADGE: Record<string, string> = {
  ACTIVE: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  PAUSED: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  COMPLETED: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  LEARNING: "bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
  ARCHIVED: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};
export const StatusBadge: React.FC<{ status: any }> = ({ status }) => {
  const text = asText(status);
  const key = text.toUpperCase();
  return (
    <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium capitalize", STATUS_BADGE[key] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300")}>
      {text.toLowerCase()}
    </span>
  );
};

// Breadcrumb — Campaigns > Campaign > Ad set > Ad, each prior segment a link.
export const Breadcrumb: React.FC<{ trail: { label: string; to?: string }[] }> = ({ trail }) => {
  const navigate = useNavigate();
  return (
    <div className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 mb-3">
      {trail.map((t, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight size={13} className="shrink-0" />}
          {t.to ? (
            <button type="button" onClick={() => navigate(t.to!)} className="hover:text-gray-700 dark:hover:text-gray-200 hover:underline">
              {t.label}
            </button>
          ) : (
            <span className="text-gray-700 dark:text-gray-200 font-medium">{t.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Tabs row shared by every detail page (Campaign/Ad set/Ad).
export const TabsRow: React.FC<{ tabs: { id: string; label: string }[]; active: string; onChange: (id: string) => void }> = ({ tabs, active, onChange }) => (
  <div className="flex items-center gap-5 border-b border-gray-100 dark:border-gray-800 mb-5 overflow-x-auto">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        onClick={() => onChange(t.id)}
        className={cn(
          "pb-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors",
          active === t.id ? "text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400" : "text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200"
        )}
      >
        {t.label}
      </button>
    ))}
  </div>
);

export const CheckDropdown: React.FC<{ value: string; options: string[]; onChange: (v: string) => void; icon?: React.ReactNode; prefix?: string }> = ({
  value,
  options,
  onChange,
  icon,
  prefix,
}) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap"
      >
        {icon}
        {prefix ? `${prefix}: ${value}` : value}
        <ChevronDown size={14} className={cn("text-gray-400 dark:text-gray-500 transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 min-w-full w-max rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden py-1">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-3.5 py-2 text-sm whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
            >
              <Check size={14} className={cn("shrink-0", opt === value ? "text-indigo-600 dark:text-indigo-400" : "text-transparent")} />
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const DateRangeDropdown: React.FC<{ value: DateRange; onChange: (v: DateRange) => void }> = ({ value, onChange }) => (
  <CheckDropdown value={value} options={DATE_RANGES} onChange={(v) => onChange(v as DateRange)} icon={<Calendar size={14} className="text-gray-400 dark:text-gray-500" />} />
);

// A metric card — icon/label/value/delta vs previous period. `invert` flips
// the up/down colour convention for cost-type metrics, per the doc's own
// "For cost metrics a decrease is good" note.
export const MetricCard: React.FC<{ label: string; metric: Metric; format: (v: any) => string; invert?: boolean; empty?: string }> = ({ label, metric, format, invert, empty = "N/A" }) => {
  const delta = formatDelta(metric.changePct);
  const isGood = metric.changePct == null ? null : invert ? metric.changePct < 0 : metric.changePct > 0;
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
      <p className={cn("text-xl font-semibold", metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}>
        {metric.value == null ? empty : format(metric.value)}
      </p>
      {delta ? (
        <p className={cn("text-xs mt-1 flex items-center gap-1", isGood === null ? "text-gray-400 dark:text-gray-500" : isGood ? "text-emerald-600 dark:text-emerald-400" : "text-rose-500 dark:text-rose-400")}>
          {metric.changePct! > 0 ? "↑" : metric.changePct! < 0 ? "↓" : "–"} {delta} vs previous period
        </p>
      ) : (
        <p className="text-xs text-gray-300 dark:text-gray-600 mt-1">{metric.value == null ? "Not available" : "No change"}</p>
      )}
    </div>
  );
};

export const LoadingRow: React.FC<{ label: string }> = ({ label }) => (
  <p className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 py-10 justify-center">
    <Loader2 size={14} className="animate-spin" /> {label}
  </p>
);

export const EmptyRow: React.FC<{ label: string }> = ({ label }) => <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-10">{label}</p>;

export const LockedCard: React.FC<{ label: string; description: string }> = ({ label, description }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
    <p className="text-xl font-semibold text-gray-300 dark:text-gray-700 blur-[4px] select-none">—</p>
    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 leading-snug">{description}</p>
  </div>
);

export const CardShell: React.FC<{ title: string; description?: string; action?: React.ReactNode; children: React.ReactNode; className?: string }> = ({
  title,
  description,
  action,
  children,
  className,
}) => (
  <div className={cn("bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5", className)}>
    <div className="flex items-center justify-between gap-3 mb-0.5">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      {action}
    </div>
    {description && <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">{description}</p>}
    {children}
  </div>
);
